import { useNavigate, createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search } from "lucide-react";

interface WalletItem {
  id: string;
  userId: string;
  balance: number;
  updatedAt: string;
  user: { id: string; email: string; name: string };
  transactionCount: number;
  lastTransaction: { type: string; amount: number; createdAt: string } | null;
}

interface WalletStats {
  totalBalance: number;
  totalWallets: number;
  activeWallets: number;
  totalTopUps: number;
  totalDeductions: number;
  totalRefunds: number;
  topUpsThisWeek: number;
  deductionsThisWeek: number;
  avgBalance: number;
  zeroBalanceCount: number;
}

const fmtCurrency = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function WalletsListPage() {
  const navigate = useNavigate();
    const [wallets, setWallets] = React.useState<WalletItem[]>([]);
  const [stats, setStats] = React.useState<WalletStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const limit = 20;

  const fetchWallets = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      params.set("sortBy", "balance");
      params.set("sortOrder", "desc");
      if (search) params.set("search", search);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/wallets?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setWallets(json.data.wallets);
      setTotal(json.data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search, page]);

  React.useEffect(() => { fetchWallets(); }, [fetchWallets]);
  React.useEffect(() => {
    fetch(`${API_ADMIN_BASE_URL}/api/admin/wallets/stats`, { credentials: "include" })
      .then((r) => r.json()).then((j) => setStats(j.data)).catch(() => {});
  }, []);
  React.useEffect(() => { document.title = "Wallets - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Wallets</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Wallets</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} wallets</p>
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Total Balance</span><span className="text-lg font-semibold">{fmtCurrency(stats.totalBalance)}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Active</span><span className="text-lg font-semibold">{stats.activeWallets}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Top-ups (Week)</span><span className="text-lg font-semibold text-green-600">{fmtCurrency(stats.topUpsThisWeek)}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Deductions (Week)</span><span className="text-lg font-semibold text-red-600">{fmtCurrency(stats.deductionsThisWeek)}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Refunds</span><span className="text-lg font-semibold">{fmtCurrency(stats.totalRefunds)}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Zero Balance</span><span className="text-lg font-semibold">{stats.zeroBalanceCount}</span></div>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="relative w-full md:w-[300px]">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input placeholder="Search by email or name..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Transactions</TableHead>
                  <TableHead>Last Transaction</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : wallets.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No wallets found</TableCell></TableRow>
                ) : wallets.map((w) => (
                  <TableRow key={w.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate({ to: `/dashboard/wallets/${w.userId}` })}>
                    <TableCell><div><p className="font-medium">{w.user.name}</p><p className="text-muted-foreground text-xs">{w.user.email}</p></div></TableCell>
                    <TableCell className="text-right font-medium">{fmtCurrency(w.balance)}</TableCell>
                    <TableCell className="text-center">{w.transactionCount}</TableCell>
                    <TableCell>{w.lastTransaction ? (<Badge variant="outline" className="text-xs">{w.lastTransaction.type} {fmtCurrency(Math.abs(w.lastTransaction.amount))}</Badge>) : (<span className="text-muted-foreground text-xs">-</span>)}</TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{fmtDate(w.updatedAt)}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number; if (totalPages <= 5) pn = i + 1; else if (page <= 3) pn = i + 1; else if (page >= totalPages - 2) pn = totalPages - 4 + i; else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/wallets/')({
  component: WalletsListPage,
})
